import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useShipperQuoteRequestDetail } from "@/hooks/useDirectQuoteRequests";
import { useChatSystem } from "@/hooks/useChatSystem";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, ArrowRight, X, ArrowUpRight, Pencil, Copy } from "lucide-react";
import ShipperQuoteResponsesList from "@/components/lr-direct/ShipperQuoteResponsesList";
import { usePageMeta } from "@/hooks/usePageMeta";

function fmtMoney(n: number | null | undefined) {
  if (n == null) return "—";
  return `$${Math.round(Number(n)).toLocaleString()}`;
}
function fmtTimeLeft(deadline: string) {
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return "Expired";
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.floor(ms / 60000)}m left`;
  if (h < 48) return `${h}h left`;
  return `${Math.floor(h / 24)}d left`;
}

function VisibilityPill({ mode }: { mode: string | null | undefined }) {
  const v =
    mode === "open_market" ? { label: "Open Market", cls: "bg-amber-50 text-amber-700 border-amber-300" }
    : mode === "both" ? { label: "Both", cls: "bg-purple-50 text-purple-700 border-purple-300" }
    : { label: "Private", cls: "bg-primary/10 text-primary border-primary/30" };
  return <Badge variant="outline" className={`text-[10px] ${v.cls}`}>{v.label}</Badge>;
}

export default function LRDirectQuoteRequestDetailPage() {
  usePageMeta({ title: "Quote · LR Direct" });
  const { id } = useParams();
  const navigate = useNavigate();
  const { source, request, loadPost, responses, loading, refresh } = useShipperQuoteRequestDetail(id);
  const { openChatWithUser } = useChatSystem();
  const [closing, setClosing] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const sortedResponses = [...responses].sort((a, b) => {
    const order = { active: 0, declined: 2, withdrawn: 3, expired: 4 } as const;
    return (order[a.status] ?? 9) - (order[b.status] ?? 9) || Number(a.rate) - Number(b.rate);
  });
  const activeCount = sortedResponses.filter((r) => r.status === "active").length;

  const closeRfq = async () => {
    if (!id) return;
    setClosing(true);
    if (source === "request") {
      const { error } = await supabase.rpc("close_direct_quote_request", { p_request_id: id });
      setClosing(false);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase
        .from("direct_loads" as any)
        .update({ status: "canceled", updated_at: new Date().toISOString() })
        .eq("id", id);
      setClosing(false);
      if (error) return toast.error(error.message);
    }
    toast.success("Quote closed");
    refresh();
  };

  const handleConvert = async () => {
    if (!id) return;
    setBusy(true);
    const { data, error } = await supabase.rpc("convert_direct_quote_to_load" as any, { p_id: id });
    setBusy(false); setConvertOpen(false);
    if (error) return toast.error(error.message);
    const r = data as { success?: boolean; error?: string; load_number?: string; load_id?: string } | null;
    if (!r?.success) return toast.error(r?.error || "Convert failed");
    toast.success(`Converted → ${r.load_number}`);
    navigate("/lr-direct/my-loads?stage=bidding");
  };

  if (loading) return <div className="max-w-5xl mx-auto p-6"><Skeleton className="h-64 w-full" /></div>;
  if (source === "request" && !request) return <div className="max-w-5xl mx-auto p-6 text-sm text-muted-foreground">Quote not found.</div>;
  if (source === "load" && !loadPost) return <div className="max-w-5xl mx-auto p-6 text-sm text-muted-foreground">Quote not found.</div>;

  // Normalize display shape
  const isLoadSource = source === "load";
  const isConverted = isLoadSource && !!loadPost?.converted_to_load_id;
  const display = isLoadSource ? {
    origin_city: loadPost.origin_city,
    origin_state: loadPost.origin_state,
    destination_city: loadPost.destination_city,
    destination_state: loadPost.destination_state,
    equipment_type: loadPost.equipment_type,
    weight_lbs: loadPost.weight_lbs,
    commodity: loadPost.commodity,
    notes: loadPost.shipper_notes_to_brokers,
    deadline: loadPost.bid_deadline ?? loadPost.created_at,
    status: loadPost.status,
    target_rate: loadPost.target_rate_min ?? loadPost.target_rate_max ?? null,
    target_rate_visible_to_brokers: false,
    volume_pattern: null as any,
    recurrence_note: null as any,
    visibility_mode: loadPost.visibility_mode,
  } : {
    origin_city: request!.origin_city,
    origin_state: request!.origin_state,
    destination_city: request!.destination_city,
    destination_state: request!.destination_state,
    equipment_type: request!.equipment_type,
    weight_lbs: request!.weight_lbs,
    commodity: request!.commodity,
    notes: request!.notes,
    deadline: request!.deadline,
    status: request!.status,
    target_rate: request!.target_rate,
    target_rate_visible_to_brokers: request!.target_rate_visible_to_brokers,
    volume_pattern: request!.volume_pattern,
    recurrence_note: request!.recurrence_note,
    visibility_mode: "private_only",
  };

  const isOpen = isLoadSource ? (display.status === "posted") : (display.status === "open");

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-5">
      <Button variant="ghost" size="sm" onClick={() => navigate("/lr-direct/my-quotes")}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <Card className="p-4 sm:p-5 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <div className="flex items-center flex-wrap gap-2 text-base sm:text-lg font-semibold">
              <span>{display.origin_city}, {display.origin_state}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <span>{display.destination_city}, {display.destination_state}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{display.equipment_type}</Badge>
              <VisibilityPill mode={display.visibility_mode} />
              {isLoadSource && (
                <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300" variant="outline">QUOTE</Badge>
              )}
              {display.weight_lbs && <span>{Number(display.weight_lbs).toLocaleString()} lbs</span>}
              {display.commodity && <span>· {display.commodity}</span>}
              {display.volume_pattern === "recurring" && (
                <Badge variant="secondary">Recurring{display.recurrence_note ? `: ${display.recurrence_note}` : ""}</Badge>
              )}
              {display.volume_pattern === "flexible" && <Badge variant="secondary">Flexible</Badge>}
              {isConverted && <Badge variant="secondary">Converted → Load</Badge>}
            </div>
            {display.notes && <p className="text-sm mt-2">{display.notes}</p>}
            {display.target_rate && (
              <p className="text-xs mt-2 text-muted-foreground">
                Target: {fmtMoney(display.target_rate)} {display.target_rate_visible_to_brokers ? "(shared with brokers)" : "(hidden from brokers)"}
              </p>
            )}
          </div>
          <div className="text-left sm:text-right shrink-0">
            <Badge variant={isOpen ? "default" : "secondary"} className="capitalize">{isOpen ? "Open" : display.status}</Badge>
            {isOpen && <div className="text-xs text-muted-foreground mt-1">{fmtTimeLeft(display.deadline)}</div>}
          </div>
        </div>


        {(isOpen || isLoadSource) && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {isLoadSource && !isConverted && activeCount > 0 && (
              <Button size="sm" onClick={() => setConvertOpen(true)}>
                <ArrowUpRight className="h-4 w-4 mr-2" /> Convert to load
              </Button>
            )}
            {isLoadSource && (
              <>
                <Button
                  size="sm" variant="outline"
                  disabled={isConverted}
                  title={isConverted ? "Converted quotes cannot be edited" : "Edit"}
                  onClick={() => navigate(`/lr-direct/post/${id}?mode=quote`)}
                >
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate(`/lr-direct/post?duplicate=${id}&mode=quote`)}>
                  <Copy className="h-4 w-4 mr-2" /> Repost
                </Button>
              </>
            )}
            {isOpen && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={closing}>
                    <X className="h-4 w-4 mr-2" /> Close
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Close this quote?</AlertDialogTitle>
                    <AlertDialogDescription>Brokers will see it as closed. You can't reopen it.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={closeRfq}>Close</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </Card>

      {/* LaneRunner market insights — shipper-only, computed from their own
          active quote responses (no RLS/security change, no broker exposure). */}
      {(() => {
        const rates = sortedResponses
          .filter((r) => r.status === "active")
          .map((r) => Number(r.rate))
          .filter((n) => Number.isFinite(n) && n > 0);
        const low = rates.length ? Math.min(...rates) : null;
        const high = rates.length ? Math.max(...rates) : null;
        const avg = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : null;
        return (
          <Card className="p-4 min-w-0 max-w-full">
            <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              LaneRunner market insights
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { label: "Low Range", value: low },
                { label: "Avg Market Rate", value: avg },
                { label: "High Range", value: high },
              ].map((t) => (
                <div key={t.label} className="min-w-0 rounded-lg bg-muted/50 p-3">
                  <p className="text-[11px] text-muted-foreground">{t.label}</p>
                  <p className="text-lg font-bold tabular-nums text-foreground">{fmtMoney(t.value)}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              {avg == null
                ? "No quotes yet — insights fill in as brokers submit rates."
                : `Based on ${rates.length} active broker ${rates.length === 1 ? "quote" : "quotes"} on this post.`}
            </p>
          </Card>
        );
      })()}


      <ShipperQuoteResponsesList
        responses={sortedResponses}
        parentCreatedAt={isLoadSource ? (loadPost?.posted_at || loadPost?.created_at) : request!.created_at}
        miles={isLoadSource ? (loadPost?.total_miles ?? null) : null}
        targetRate={display.target_rate ?? null}
        onMessageBroker={(uid, name) => openChatWithUser(uid, name)}
        onSelectResponse={isLoadSource && !isConverted ? () => setConvertOpen(true) : undefined}
        selectLabel="Convert to load"
      />

      <AlertDialog open={convertOpen} onOpenChange={setConvertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert this quote into a live load?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates a new LR Direct load with the same lane and equipment. Brokers can bid on it. The original quote and all submitted quote responses stay here for your reference — nothing is auto-accepted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvert} disabled={busy}>Convert to load</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
